import React, { useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { useIntl } from 'react-intl'
import styled from '@emotion/styled'
import { useRecoilCallback, useRecoilValue, useSetRecoilState } from 'recoil'
import { pageState, recipientsState } from 'Pages/QuickSend/recoil/state/state'
import { initQuickSendCallback } from 'Pages/QuickSend/recoil/callbacks/callbacks'
import {
  Loading,
  MessageBox,
  MESSAGE_BOX_VARIANT_INFO,
  SpacingVertical,
  SPACING_16,
  SPACING_24,
  SPACING_40,
  Title,
  TITLE_VARIANT_MODAL,
  Tooltip
} from 'mailup-web-components'
import ErrorFeedback from 'Components/ErrorFeedback'
import WarningMessages from 'Pages/QuickSend/components/WarningMessages/WarningMessages'
import BoxInfo from 'Pages/QuickSend/components/BoxInfo/BoxInfo'
import Footer from 'Pages/QuickSend/components/Footer/Footer'
import Recipients from 'Pages/QuickSend/components/Recipients/Recipients'
import { notifyOpener } from 'Helpers/notifyParent'
import useResizeObserver from 'Hooks/useResizeObserver'
import SenderInfo from 'Pages/QuickSend/components/SenderInfo/SenderInfo'

const StyledContainer = styled.div`
  background-color: var(--pure-white);
  min-height: 476px;
`

const QuickSend = () => {
  const el = useRef(null)
  const intl = useIntl()
  const { id: idMessage } = useParams()
  const { loading, loaded, error, severity, senderIsDeleted } = useRecoilValue(
    pageState
  )
  const {
    error: recipientsError,
    severity: recipientsSeverity
  } = useRecoilValue(recipientsState)
  const init = useRecoilCallback(initQuickSendCallback, [])

  const notifyResize = payload => {
    notifyOpener('pageResize', ['quickSend', payload])
  }

  useEffect(() => {
    init(idMessage)
  }, [])

  useResizeObserver(document.querySelector('body'), notifyResize)

  const MessageBoxSenderIsDeleted = () => {
    const intl = useIntl()
    const setPageState = useSetRecoilState(pageState)
    return (
      <>
        <MessageBox
          variant={MESSAGE_BOX_VARIANT_INFO}
          closeButton
          closeButtonCallback={() => {
            setPageState(prev => ({
              ...prev,
              senderIsDeleted: false
            }))
          }}
          data-mu-test='message-box-info'
        >
          <>
            {intl.formatMessage({
              id: 'Load.QuickSend.DefaultSenderInfo'
            })}
          </>
        </MessageBox>
        <SpacingVertical variant={SPACING_16} />
      </>
    )
  }

  return (
    <StyledContainer ref={el}>
      <Loading open={loading} />
      <Tooltip />
      <Title variant={TITLE_VARIANT_MODAL}>
        {intl.formatMessage({ id: 'Load.QuickSend.Title' })}
      </Title>
      <SpacingVertical variant={SPACING_24} />
      <ErrorFeedback
        error={error}
        severity={severity}
        onClickReload={() => init(idMessage)}
      />
      {error && <SpacingVertical variant={SPACING_24} />}
      {!error && loaded && (
        <>
          <ErrorFeedback
            error={recipientsError}
            severity={recipientsSeverity}
          />
          <SpacingVertical variant={SPACING_24} />
          <WarningMessages />
          <BoxInfo />

          <Recipients />
          {senderIsDeleted && <MessageBoxSenderIsDeleted />}
          <SenderInfo />
          <SpacingVertical variant={SPACING_40} />
          <Footer />
        </>
      )}
    </StyledContainer>
  )
}

export default QuickSend
